<template>
  <!-- PP team expects to switch gallery implementation to ProductDetailPageGallery in the near future, whatever the "automated reviews" flag status. -->
  <ProductDetailPageGallery
    v-if="productResponse && isAutomatedReviewsInCarouselEnabled"
    :are-reviews-pending
    :product-info="{
      images: productResponse.images,
      type: productResponse.type,
      brand: productResponse.brand,
      categoryId: productResponse.tracking.categoryId,
    }"
    :reviews-from-product="reviewsFromProductResponse ?? undefined"
    :tracking-product-model="productResponse.model ?? ''"
    @sticker-click="handleStickerClick"
  >
    <template #sticker="{ size }">
      <Sticker
        :size
        :bottom-text="stickerBottomText"
        :icon-name="stickerIconName"
        :top-text="stickerTopText"
      />
    </template>
  </ProductDetailPageGallery>
  <!-- Keep GalleryNew for JP as long as 'step by step' A/B test is running. Later APAC team will switch to ProductDetailPageGallery (APAC-1203). -->
  <GalleryNew
    v-else-if="productResponse"
    :product-info="{
      images: productResponse.images,
      type: productResponse.type,
      brand: productResponse.brand,
      categoryId: productResponse.tracking.categoryId,
    }"
    :review="reviewInCarouselResponse ?? undefined"
    should-preload-first-img
    :tracking-product-model="productResponse.model ?? ''"
    @sticker-click="handleStickerClick"
  >
    <template #sticker="{ size }">
      <Sticker
        :size
        :bottom-text="stickerBottomText"
        :icon-name="stickerIconName"
        :top-text="stickerTopText"
      />
    </template>
  </GalleryNew>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode.ts'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { openModal } from '@ds/components/ModalBase'

import GalleryNew from '~/scopes/product-core/components/presentationals/GalleryNew/GalleryNew.vue'
import ProductDetailPageGallery from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/ProductDetailPageGallery.vue'
import Sticker from '~/scopes/product-core/components/presentationals/ProductDetailPageGallery/components/Sticker/Sticker.vue'
import { useFetchProduct } from '~/scopes/product-core/composables/useFetch/useFetchProduct/useFetchProduct'
import { useFetchVerifiedRefurbishCarousel } from '~/scopes/product-core/composables/useFetch/useFetchVerifiedRefurbishCarousel/useFetchVerifiedRefurbishCarousel'
import { MODAL_NAMES } from '~/scopes/product-core/constants'

import { useFetchAutomatedReviewsForCarousel } from './composables/useFetchAutomatedReviewsForCarousel/useFetchAutomatedReviewsForCarousel'
import { useFetchReviewInGalleryNew } from './composables/useFetchReviewInGalleryNew/useFetchReviewInGalleryNew'

const { data: productResponse } = useFetchProduct()
const { data: reviewInCarouselResponse } = useFetchReviewInGalleryNew()
const { data: reviewsFromProductResponse, pending: areReviewsPending } =
  useFetchAutomatedReviewsForCarousel()
const { data: verifiedRefurbishCarouselResponse } =
  useFetchVerifiedRefurbishCarousel()
const experiments = useExperiments()
const { trackClick } = useTracking()
const { market: currentMarket } = useMarketplace()

const isJapan = computed(
  () => currentMarket.countryCode === MarketCountryCode.JP,
)

const isAutomatedReviewsInCarouselEnabled = computed(
  () =>
    experiments['experiment.ppAutomatedReviewsInCarousel'] ===
      'withAutomatedReviews' && !isJapan.value,
)

const isProfessionallyCleanedEnabled = computed(
  () =>
    experiments['experiment.ppProfessionallyCleanedSticker'] ===
    'withProfessionallyCleaned',
)

const stickerTopText = computed(() =>
  isProfessionallyCleanedEnabled.value
    ? verifiedRefurbishCarouselResponse.value?.sticker?.topText
    : undefined,
)
const stickerBottomText = computed(() =>
  isProfessionallyCleanedEnabled.value
    ? verifiedRefurbishCarouselResponse.value?.sticker?.bottomText
    : undefined,
)
const stickerIconName = computed(() =>
  isProfessionallyCleanedEnabled.value
    ? verifiedRefurbishCarouselResponse.value?.sticker?.icon?.name
    : undefined,
)

function handleStickerClick() {
  openModal(
    isProfessionallyCleanedEnabled.value
      ? MODAL_NAMES.VERIFIED_REFURBISHED_CAROUSEL
      : MODAL_NAMES.VERIFIED_REFURBISHED,
  )
  trackClick({
    zone: 'verified_refurbished_sticker',
    name: 'verified_refurbished',
  })
}
</script>
